import { get } from 'lodash';
import moment from 'moment-timezone';

import {
  APPT_STATUS,
  HBOT_CALLBACK,
  IV_CALLBACK,
  IV_HIGH_DOSE_CALLBACK
} from '../../common/constants';
import AuthUser from './AuthUser';
import { Dialog, styled } from '@mui/material';
import { convertToStoreLocalTime } from './time';
import { getStoreLocalTime } from '@/common/utils/time';
import {
  customerId2Gql,
  gql2Id,
  slotId2Gql,
  therapistId2Gql
} from './apiConversionUtils';
import CONFIG from '@/config';
import { formDataInstance } from '@/common/axios';
import { AppointmentEventStatus } from '../constants/appointment-status';
import type {
  StaffAppointmentListResponse,
  StaffAppointmentListResponseMembership,
  StaffUpdateAppointmentRequestStatus
} from '@/api/booking/booking-api.schemas';
import {
  SetupTestHarness,
  APPT_TEST_LABELS,
  TEST_HARNESS_MODULES
} from '@/common/utils/testUtils';
import z from 'zod';
import { NutrientInfo } from '@/hooks/useNutrients';

const storeDays = [
  {
    id: 0,
    day: 'Sunday'
  },
  {
    id: 1,
    day: 'Monday'
  },
  {
    id: 2,
    day: 'Tuesday'
  },
  {
    id: 3,
    day: 'Wednesday'
  },
  {
    id: 4,
    day: 'Thursday'
  },
  {
    id: 5,
    day: 'Friday'
  },
  {
    id: 6,
    day: 'Saturday'
  }
];

const bookingSources = {
  pow: 'POW',
  Truelark: 'Truelark',
  'booking-api-consumer-mobile': 'Consumer Mobile',
  'booking-api-consumer-web': 'Consumer Web'
};

interface AppointmentPopupModel {
  check_out: string;
  mark_arrived: string;
  mark_confirmed: string;
  cancel: string;
  modify: string;
  late_not_arrived: string;
  purchased: string;
}

export const NO_PRICE_ITEM_ID = 99;

export function getAppointmentLatestStatus(status: number) {
  // Logic to mutually exclude certain states into logical counts
  if (status & APPT_STATUS.CANCELLED) {
    return 'Cancelled';
  } else if (status & APPT_STATUS.PURCHASED) {
    return 'Purchased';
  } else if (status & APPT_STATUS.PENDING_TICKET) {
    return 'Pending Ticket';
  } else if (status & APPT_STATUS.UNAVAILABLE) {
    return 'Unavailable';
  } else if (status & APPT_STATUS.NO_SHOW) {
    return 'Late/No Show';
  } else if (status & APPT_STATUS.ARRIVED) {
    return 'Arrived';
  } else if (status & APPT_STATUS.CONFIRMED) {
    return 'Confirmed';
  } else if (status & APPT_STATUS.BOOKED) {
    return 'Booked';
  } else {
    return 'Unknown';
  }
}

export const dateFmt = 'YYYY-MM-DD';
export const dateSlashFmt = 'MM/DD/YYYY';
export const timeFmt = 'h:mma';
export const dateTimeFmt = 'YYYY-MM-DDTHH:mm';
export const dateTimeLongFmt = 'YYYY-MM-DD[T]HH:mm:ss';
export const dateTimeLongFmtWithDay = 'MMM D, YYYY h:mma';

export const updateStatusType = (
  apt: TimelineAppointment,
  statusType: number,
  updatedAt: string
) => ({
  ...apt,
  customElements: {
    ...apt.customElements,
    appointment: {
      ...apt.customElements.appointment,
      statusType: statusType,
      updatedAt: updatedAt
    }
  }
});

export type TimelineAppointment = ReturnType<
  typeof mapAppointmentDataForTimeline
>;

export const mapAppointmentDataForTimeline = (
  data: StaffAppointmentListResponse
) => {
  const dripIngredients =
    Array.isArray(data.dripIngredients) && data.dripIngredients?.length
      ? JSON.parse(data.dripIngredients)
      : [];

  return {
    id: data.id,
    title: `${data.customer?.firstName || 'unavailable'} ${
      data.customer?.lastName || 'Time'
    }`,
    start: convertToStoreLocalTime(data.start, dateTimeLongFmt),
    end: convertToStoreLocalTime(data.end, dateTimeLongFmt),
    dripIngredients: dripIngredients,
    resourceEditable: true,
    customElements: {
      resourceId: slotId2Gql(data.slot.id),
      appointment: {
        ...data,
        id: data.id,
        therapist: therapistId2Gql(data.therapist),
        slot: {
          ...data.slot,
          id: slotId2Gql(data.slot.id)
        },
        purchasableItem: {
          ...data.purchasableItem,
          id: data.purchasableItem?.id,
          purchaseCategory: {
            id: data.purchasableItem?.purchaseCategory?.id
          }
        },
        customer: {
          ...data?.customer,
          id: customerId2Gql(data.customer?.id),
          memberships: data.customer?.memberships ?? [],
          membership: data.customer?.memberships ?? [],
          leadCampaign: data.customer?.leadCampaign
        },
        updated_at: moment(data.updatedAt).format(dateTimeLongFmt)
      }
    },
    resourceId: slotId2Gql(data.slot.id),
    therapist: therapistId2Gql(data.therapist)
  };
};

// Calculate the blocks of time that are not available either due to being outside of store hours
// or if there is an existing appointment.  The key is time in milliseconds, value is true if the
// time block is available for booking.
//
// IF YOU CHANGE ANY THING IN THIS FUNCTION PLEASE UPDATE THE COMMENTS AND BE CAREFUL AS ITS VERY IMPORTANT CODE.
// DOUBLE BOOKING APPOINTMENTS WOULD BE A VERY BAD THING.  POW-269 will add a unit test.
//
export type ResourceHours = {
  endTime: string;
  startTime: string;
  daysOfWeek: number[];
};

export const calculateFreeStartTimes = (
  existingAppointments: Array<TimelineAppointment>, // existing appointments for the day that govern whether the apt can be accommodated
  storeHours: Array<ResourceHours>, // store hours
  resourceHours: Array<ResourceHours>, // availability of resource, if emtpy assume all store hours
  currentDate: string, // date of appointment being considered
  durationMins: number, // duration of appointment block being considered
  timeBlockMinutes: number, // size of timeblocks to evaluate on
  slotId: string | undefined = undefined // slot to filter on if provided
) => {
  // if a slot is provided filter only appointments for that slot
  if (slotId) {
    existingAppointments = existingAppointments.filter(
      a => get(a, 'customElements.appointment.slot.id') === slotId
    );
  }

  // Determine store hours for the currentDate
  let dayOfWeek = moment(currentDate).isoWeekday();

  // moment has Sunday as 7 and our system has it as 0
  dayOfWeek = dayOfWeek === 7 ? 0 : dayOfWeek;

  const dayStoreHours = storeHours[dayOfWeek];
  const dayResourceHours =
    resourceHours
      ?.filter(rhs => rhs?.daysOfWeek[0] === dayOfWeek)
      ?.map(rhs => ({
        start: moment(currentDate).set({
          hour: Number(rhs.startTime.split(':')[0]),
          minute: Number(rhs.startTime.split(':')[1])
        }),
        end: moment(currentDate).set({
          hour: Number(rhs.endTime.split(':')[0]),
          minute: Number(rhs.endTime.split(':')[1])
        })
      })) || [];

  const startStoreDayTime = moment(
    `${moment(currentDate).format(dateFmt)} ${dayStoreHours?.startTime}`
  );
  const endStoreDayTime = moment(
    `${moment(currentDate).format(dateFmt)} ${dayStoreHours?.endTime}`
  );
  const startOfStoreHoursForDay = moment(currentDate)
    .clone()
    .set({
      hour: startStoreDayTime.get('hour'),
      minute: startStoreDayTime.get('minute')
    });
  const endOfStoreHoursForDay = moment(currentDate)
    .clone()
    .set({
      hour: endStoreDayTime.get('hour'),
      minute: endStoreDayTime.get('minute')
    });

  const occupiedTimeBlocks: Record<string, number | boolean | undefined> = {};
  const startDateTime = moment(currentDate)
    .hours(0)
    .minute(0)
    .second(0)
    .milliseconds(0);
  // For each hour
  for (let h = 0; h < 24; h++) {
    // For each block
    for (let m = 0; m < 60; m += timeBlockMinutes) {
      const blockDateTime = startDateTime
        .clone()
        .add(h, 'hours')
        .add(m, 'minutes');

      // If the timeblock is outside of store then set to true (is occupied)
      const beforeOpen = blockDateTime.isBefore(startOfStoreHoursForDay);
      const afterClose = blockDateTime.isSameOrAfter(endOfStoreHoursForDay);
      const hasDuration = durationMins > 0;
      occupiedTimeBlocks[blockDateTime.valueOf()] =
        beforeOpen || afterClose ? undefined : !hasDuration;
    }
  }
  // Now if resource availability has constraints, walk the available resource time blocks and
  // mark all other time blocks as occupied (true)
  if (dayResourceHours.length > 0) {
    // Mark all slots in store hours as occupied (true)
    Object.keys(occupiedTimeBlocks).forEach(
      otb => (occupiedTimeBlocks[otb] = true)
    );
    // Then set time blocks that are available per dayResourceHours to false (available)
    dayResourceHours.forEach(rb => {
      const start = moment(rb.start).valueOf();
      const end = moment(rb.end).valueOf();
      for (let t = start; t < end; t += timeBlockMinutes * 60 * 1000) {
        occupiedTimeBlocks[t] = false;
      }
    });
  }

  // Walk the sorted appointments and mark time blocks as occupied (true) or unoccupied (false)
  existingAppointments.forEach(a => {
    const start = moment(String(a.start)).valueOf();
    const end = moment(String(a.end)).valueOf();
    // Mark all time blocks this appointment spans as occupied, timeBlockMinutes (mins) * seconds * ms
    for (let t = start; t < end; t += timeBlockMinutes * 60 * 1000) {
      occupiedTimeBlocks[t] = true;
    }
  });

  // Walking the timeblocks backwards the value of each timeblock will be the number of forward looking,
  // consecutive timeblocks that are open.  This tell us if any given start time can accommnodate a given
  // duration.  This makes recalculation of available start times a duration complexity o(n)
  // Iterating backwards through the time slots and...
  const KeyList = Object.keys(occupiedTimeBlocks).map(Number);
  let i = KeyList.length - 1;
  while (i >= 0) {
    const key = KeyList[i];
    if (key === undefined) {
      i--;
      continue;
    }
    // For each timeblock that is true mark it with 1 and then each consecutive unoccupided
    // timeblock assign a value of n + 1.  These numbers represent the number of consecutive
    // timeblocks available for any specific start time.
    if (occupiedTimeBlocks[key] === undefined) {
      // outside store hours
      i--;
    } else if (occupiedTimeBlocks[key]) {
      // not available
      occupiedTimeBlocks[key] = 0;
      i--;
    } else {
      // available and has consecutiveTimeBlocks available slots starting at this one
      let consecutiveTimeBlocks = 1;
      occupiedTimeBlocks[key] = consecutiveTimeBlocks++;
      i--;

      let innerKey = KeyList[i];
      while (
        i >= 0 &&
        innerKey !== undefined &&
        !occupiedTimeBlocks[innerKey] &&
        occupiedTimeBlocks[innerKey] !== undefined
      ) {
        occupiedTimeBlocks[innerKey] = consecutiveTimeBlocks++;
        i--;
        innerKey = KeyList[i];
      }
    }
  }

  // Lastly is to put these in a format the react-datepicker can use to show the times
  const includeTimes: Date[] = [];
  Object.keys(occupiedTimeBlocks).forEach(otb => {
    const count = occupiedTimeBlocks[otb] as number;
    const numTimeBlocksForDuration = Math.ceil(durationMins / timeBlockMinutes);
    if (count === undefined) {
      // Excluding the time if its outside of store hours:
    } else if (count >= numTimeBlocksForDuration) {
      // If the number of contiguous time blocks is enough for the current appointment duration the include it:
      includeTimes.push(new Date(parseInt(otb)));
    }
  });

  // Leave these here for easy of debugging
  /*Object.keys(occupiedTimeBlocks).map(t =>
    console.log(new Date(parseInt(t)) + ': ' + occupiedTimeBlocks[t])
  );*/
  //console.log(JSON.stringify(includeTimes.map(t => moment(t).format('HH:mm')), null, 2));
  // prettier-ignore

  return includeTimes;
};

export const isApptUnavailable = (apptStatus: number) => {
  return (apptStatus & APPT_STATUS.UNAVAILABLE) != 0;
};
export const isApptPurchaseComplete = (apptStatus: number) => {
  return (apptStatus & APPT_STATUS.PURCHASED) != 0;
};
export const isApptCancelled = (apptStatus: number) => {
  return (apptStatus & APPT_STATUS.CANCELLED) != 0;
};

export const getNewValueForStatus = (type: string, curStatus: number) => {
  let mask = 0;
  if (type === 'arrived') {
    mask = APPT_STATUS.ARRIVED;
  } else if (type === 'confirmed') {
    mask = APPT_STATUS.CONFIRMED;
  } else if (type === 'late') {
    mask = APPT_STATUS.NO_SHOW;
  } else if (type === 'unavailable') {
    mask = APPT_STATUS.UNAVAILABLE | APPT_STATUS.BOOKED;
  }
  // Use bitwsie exclusive or to toggle just this one bit
  return curStatus ^ mask;
};

export const popupActions: AppointmentPopupModel = {
  check_out: 'Checkout',
  mark_arrived: 'Arrived',
  mark_confirmed: 'Confirmed',
  cancel: 'Cancel',
  modify: 'Edit',
  late_not_arrived: 'Late/No Show',
  purchased: 'Purchased'
};

export const popupActionsList = [
  popupActions.check_out,
  popupActions.mark_arrived,
  popupActions.mark_confirmed,
  popupActions.cancel,
  popupActions.modify,
  popupActions.late_not_arrived
];

export const getPreparedUpdateAppointment = (
  appointment: Partial<StaffAppointmentListResponse>
) => {
  const loggedInUser = AuthUser.getAuthUser();
  const storeId = loggedInUser ? loggedInUser.store?.id : 0;
  return {
    ...appointment,
    customer: appointment.customer?.id,
    store: storeId,
    purchasableItem: appointment.purchasableItem?.id,
    purchaseCategory: {
      id: appointment.purchasableItem?.purchaseCategory?.id
    },
    dripIngredients:
      typeof appointment.dripIngredients === 'string'
        ? appointment.dripIngredients
        : JSON.stringify(appointment.dripIngredients),
    start: moment(appointment.start).format(dateTimeLongFmt),
    end: moment(appointment.end).toDate(),
    notes: appointment.notes,
    slot: appointment.slot?.id,
    modifier: appointment.modifier,
    statusType: appointment.statusType
  };
};

export const generateStoreHours = () => {
  const authUser = AuthUser.getAuthUser();
  if (!authUser?.store) {
    return [];
  }
  const { store } = authUser;
  const allHours: ResourceHours[] = [];
  for (let index = 0; index < storeDays.length; index++) {
    const currentDay = storeDays.find(d => d.id === index)?.day.toLowerCase();
    if (!currentDay) {
      continue;
    }
    const storeWithDays = { ...store, ...hoursToDaysObj(store.hours) } as {
      [key: string]: any;
    };
    const dayObj = {
      daysOfWeek: [index],
      startTime: String(
        storeWithDays[currentDay]
          ? storeWithDays[currentDay]?.map(
              (d: { start: number; end: number }) => {
                const start = d?.start;
                if (start == null) {
                  return '';
                }
                const startStr = start.toString();
                return startStr.length < 2
                  ? `0${startStr}:00`
                  : `${startStr}:00`;
              }
            )
          : ''
      ),
      endTime: String(
        storeWithDays[currentDay]
          ? storeWithDays[currentDay].map(
              (d: { start: number; end: number }) => {
                const end = d?.end;
                if (end == null) {
                  return '';
                }
                const endStr = end.toString();
                return endStr.length < 2 ? `0${endStr}:00` : `${endStr}:00`;
              }
            )
          : ''
      )
    };
    if (dayObj.endTime && dayObj.startTime) {
      allHours.push(dayObj);
    }
  }
  return allHours;
};

let hasShownStoreHoursError = false;
const hoursToDaysObj = (
  hours: string
): Record<string, { start: number; end: number }[]> => {
  let hoursJson;
  try {
    hoursJson = JSON.parse(hours) || {};
  } catch {
    // just don't crash
    hoursJson = {};
    if (!hasShownStoreHoursError) {
      alert(
        'There is a problem processing store hours.  Please contact Restore IT Support for assistance.'
      );
      hasShownStoreHoursError = true;
    }
  }
  const hoursToDays: Record<string, { start: number; end: number }[]> = {};
  Object.keys(hoursJson).forEach(key => {
    Array.from(key).forEach(letter => {
      const day = storeDays.find(d => d.id.toString() === letter)?.day;
      if (day) {
        hoursToDays[day.toLowerCase()] = [
          {
            start: hoursJson[key][0][0],
            end: hoursJson[key][0][1]
          }
        ];
      }
    });
  });
  return hoursToDays;
};

export const getStoreHoursMinMax = (
  todayDate: moment.MomentInput
): { slotMinTime?: string; slotMaxTime?: string } => {
  const storeHours = generateStoreHours();
  const selectedDay = parseInt(moment.utc(todayDate).format('d'));
  const storeTime = storeHours
    .map(d => {
      if (d.daysOfWeek.indexOf(selectedDay) >= 0) {
        return {
          slotMinTime: `${d.startTime}:00`,
          slotMaxTime: `${d.endTime}:00`
        };
      }
      return false;
    })
    .filter(
      (i): i is { slotMinTime: string; slotMaxTime: string } => i !== false
    );
  return storeTime.length ? (storeTime[0] ?? {}) : {};
};

export const getAppointmentsCounts = (
  appointments: TimelineAppointment[]
): Partial<Record<AppointmentEventStatus, number>> => {
  const obj: Partial<Record<AppointmentEventStatus, number>> = {
    BOOKED: 0,
    CONFIRMED: 0,
    ARRIVED: 0,
    CANCELLED_OR_LATE: 0,
    PENDING_TICKET: 0,
    PURCHASED: 0
  };
  (appointments || []).forEach(appt => {
    const statusType = appt?.customElements?.appointment?.statusType;
    if (statusType === null || statusType === undefined) {
      return;
    }
    // Logic to mutually exclude certain states into logical counts
    if (statusType & APPT_STATUS.PURCHASED) {
      obj.PURCHASED = (obj.PURCHASED ?? 0) + 1;
    } else if (statusType & APPT_STATUS.PENDING_TICKET) {
      obj.PENDING_TICKET = (obj.PENDING_TICKET ?? 0) + 1;
    } else if (statusType & (APPT_STATUS.CANCELLED | APPT_STATUS.UNAVAILABLE)) {
      // just excluding some cases from the else
    } else {
      if (statusType & APPT_STATUS.NO_SHOW) {
        obj.CANCELLED_OR_LATE = (obj.CANCELLED_OR_LATE ?? 0) + 1;
      } else if (statusType & APPT_STATUS.ARRIVED) {
        obj.ARRIVED = (obj.ARRIVED ?? 0) + 1;
      } else if (statusType & APPT_STATUS.CONFIRMED) {
        obj.CONFIRMED = (obj.CONFIRMED ?? 0) + 1;
      } else {
        obj.BOOKED = (obj.BOOKED ?? 0) + 1;
      }
    }
  });
  return obj;
};

export const isIngredientCallback = (cb: string) => {
  return cb === IV_CALLBACK || cb === IV_HIGH_DOSE_CALLBACK;
};
export const isHbotCallback = (cb: string) => {
  return cb === HBOT_CALLBACK;
};

export const getDate = (startDate: moment.MomentInput, daysOut = 0) =>
  moment(startDate).add(daysOut, 'days').format(dateFmt);

export const startDtCompare = (
  a: { start: moment.MomentInput },
  b: { start: moment.MomentInput }
) => {
  if (!a.start || !b.start) {
    return 0;
  }
  if (a.start < b.start) {
    return -1;
  } else if (a.start > b.start) {
    return 1;
  } else {
    return 0;
  }
};

// return true if the customer has any legacy memberships
export const getHasLegacyMembership = (a: StaffAppointmentListResponse) =>
  a?.customer?.memberships?.some(
    m => !m?.isPow && m?.isLive && m?.purchasableItem
  );

// return if the customer has an active membership of any kind
export const getIsMember = (a: StaffAppointmentListResponse) =>
  a?.customer?.memberships?.some(m => m?.isLive);

export const getMembershipStudio = (a: StaffAppointmentListResponse) => {
  // Find the first membership object where isLive is true
  const liveMembership = a?.customer?.memberships?.find(m => m?.isLive);

  // Return the purchaseStore.storeCode of the found membership object
  return liveMembership?.purchaseStore?.storeCode;
};

export const mergeAppointmentStatusUpdates = (
  appointments: TimelineAppointment[],
  results: StaffAppointmentListResponse[]
) => {
  return appointments.map(a => {
    const update = results.find(u => u.id === a.id);
    return update
      ? {
          ...a,
          customElements: {
            ...a.customElements,
            appointment: {
              ...a.customElements.appointment,
              statusType: update.statusType
            }
          }
        }
      : a;
  });
};

export const displayBookingSource = (origin: string) => {
  if (!origin) {
    return 'Unknown';
  } else if (origin in bookingSources) {
    return bookingSources[origin as keyof typeof bookingSources];
  } else {
    return origin;
  }
};

export const membershipsWithUnsignedAgreement = (
  memberships: StaffAppointmentListResponseMembership[]
) => memberships?.filter(m => m.isAgreementSigned === false);

export const hasActiveLegacyMemb = (
  memberships: StaffAppointmentListResponseMembership[]
) => memberships?.some(m => (!m.isPow || m.isPow === null) && m.isLive);

export const hasLiveMembership = (
  memberships: StaffAppointmentListResponseMembership[]
) => memberships?.some(m => m.isLive);

export const falsey = (v: unknown) =>
  v === undefined || v === null || v == false;

export const hasMembershipNeedingAgreementSigned = (
  memberships: StaffAppointmentListResponseMembership[]
) => getMembershipsMissingSignature(memberships)?.length !== 0;

export const getMembershipsMissingSignature = (
  memberships: StaffAppointmentListResponseMembership[]
) => {
  const storeLocalTime = getStoreLocalTime();
  return (
    memberships?.filter(m => {
      const futureStart = moment(
        convertToStoreLocalTime(m.membershipStartDate)
      ).isAfter(storeLocalTime);
      const pastCancelDate = moment(
        convertToStoreLocalTime(m.cancelDate)
      ).isBefore(storeLocalTime);
      const liveNowOrInFuture = (m.isLive || futureStart) && !pastCancelDate;
      const agreementNotSigned = falsey(m.isAgreementSigned);
      const isValidPurchaseItem = m.purchasableItem != null;
      const doesNotRollIntoAnother = !m.rolledForMembershipId;
      return (
        liveNowOrInFuture &&
        agreementNotSigned &&
        isValidPurchaseItem &&
        doesNotRollIntoAnother
      );
    }) || []
  );
};

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogTitle-root, & .MuiDialogActions-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogContent-root': {
    paddingBlock: 0,
    paddingInline: theme.spacing(2)
  },
  '& .MuiFormControlLabel-label': {
    fontWeight: '400'
  }
}));

export const getAppointmentStatusEnums = (
  statusMask: number,
  onlyMasksToAdd = false
) => {
  const updatedStatus: StaffUpdateAppointmentRequestStatus = {};
  if (!onlyMasksToAdd || (statusMask & APPT_STATUS.BOOKED) !== 0) {
    updatedStatus['BOOKED'] = (statusMask & APPT_STATUS.BOOKED) !== 0;
  }
  if (!onlyMasksToAdd || (statusMask & APPT_STATUS.ARRIVED) !== 0) {
    updatedStatus['ARRIVED'] = (statusMask & APPT_STATUS.ARRIVED) !== 0;
  }
  if (!onlyMasksToAdd || (statusMask & APPT_STATUS.CONFIRMED) !== 0) {
    updatedStatus['CONFIRMED'] = (statusMask & APPT_STATUS.CONFIRMED) !== 0;
  }
  if (!onlyMasksToAdd || (statusMask & APPT_STATUS.PENDING_TICKET) !== 0) {
    updatedStatus['PENDING_TICKET'] =
      (statusMask & APPT_STATUS.PENDING_TICKET) !== 0;
  }
  if (!onlyMasksToAdd || (statusMask & APPT_STATUS.PURCHASED) !== 0) {
    updatedStatus['PURCHASED'] = (statusMask & APPT_STATUS.PURCHASED) !== 0;
  }
  if (!onlyMasksToAdd || (statusMask & APPT_STATUS.CANCELLED) !== 0) {
    updatedStatus['CANCELLED'] = (statusMask & APPT_STATUS.CANCELLED) !== 0;
  }
  if (!onlyMasksToAdd || (statusMask & APPT_STATUS.NEW_CUSTOMER) !== 0) {
    updatedStatus['NEW_CUSTOMER'] =
      (statusMask & APPT_STATUS.NEW_CUSTOMER) !== 0;
  }
  if (!onlyMasksToAdd || (statusMask & APPT_STATUS.UNAVAILABLE) !== 0) {
    updatedStatus['UNAVAILABLE'] = (statusMask & APPT_STATUS.UNAVAILABLE) !== 0;
  }
  if (!onlyMasksToAdd || (statusMask & APPT_STATUS.NO_SHOW) !== 0) {
    updatedStatus['NO_SHOW'] = (statusMask & APPT_STATUS.NO_SHOW) !== 0;
  }
  return updatedStatus;
};

// these getters work on appointment models from the schedule appointment modal and with those from the calendar view,
export const getDripIng = (
  appointment: Partial<StaffAppointmentListResponse>
) => {
  let dripIng: any[] = [];
  if (appointment.dripIngredients) {
    if (typeof appointment.dripIngredients === 'string') {
      try {
        const parsed = JSON.parse(appointment.dripIngredients);
        if (Array.isArray(parsed)) {
          dripIng = parsed;
        }
      } catch {
        // ignore if parsing fails
      }
    } else if (Array.isArray(appointment.dripIngredients)) {
      dripIng = appointment.dripIngredients;
    }
  }
  return dripIng;
};

export const getEndTime = (
  appointment: Partial<StaffAppointmentListResponse> & { duration?: number }
) => {
  // when saving from modal we need to convert duration to end time
  if (appointment.duration) {
    return moment(appointment.start)
      .add(appointment.duration, 'minutes')
      .format(dateTimeLongFmt);
  } else {
    return moment(appointment.end).format(dateTimeLongFmt);
  }
};

export const getSlotId = (
  appointment: Partial<StaffAppointmentListResponse>
) => {
  if (typeof appointment.slot === 'string') {
    return gql2Id(appointment.slot);
  } else if (typeof appointment.slot === 'number') {
    return appointment.slot;
  } else {
    return appointment.slot?.id;
  }
};

export const getCustomerId = (
  appointment: Partial<StaffAppointmentListResponse>
) => {
  if (typeof appointment.customer === 'string') {
    return gql2Id(appointment.customer);
  } else if (typeof appointment.customer === 'number') {
    return appointment.customer;
  } else {
    return appointment.customer?.id;
  }
};

export const getPiId = (appointment: Partial<StaffAppointmentListResponse>) => {
  if (typeof appointment.purchasableItem === 'string') {
    return gql2Id(appointment.purchasableItem);
  } else if (typeof appointment.purchasableItem === 'number') {
    return appointment.purchasableItem;
  } else {
    return appointment.purchasableItem?.id;
  }
};

// convert the appointment data to the format expected by the API
export const prepareAppointmentForSave = (
  newValues: Partial<StaffAppointmentListResponse> & { duration?: number },
  oldValues: StaffAppointmentListResponse | null,
  sendConfirmationEmail: boolean,
  appointmentId: number | undefined = undefined
) => {
  const loggedInUser = AuthUser.getAuthUser();
  const storeId = loggedInUser ? loggedInUser.store?.id : 0;
  const isNew = appointmentId === undefined;
  const isCustomerAppointment = !isApptUnavailable(newValues.statusType ?? 0);

  let newS;
  let oldS;
  let startHasChanged = false;

  if (newValues.start && oldValues?.start) {
    newS = new Date(newValues.start).valueOf();
    oldS = new Date(
      convertToStoreLocalTime(oldValues.start, 'YYYY-MM-DDTHH:mm:SS')
    ).valueOf();
    startHasChanged = newValues.start !== undefined && oldS != newS;
  }

  let newE;
  let oldE;
  let endHasChanged = false;

  if (newValues.end && oldValues?.end) {
    newE = new Date(newValues.end).valueOf();
    oldE = new Date(
      convertToStoreLocalTime(oldValues.end, 'YYYY-MM-DDTHH:mm:SS')
    ).valueOf();
    endHasChanged = newValues.end !== undefined && oldE != newE;
  }

  const variables: any = {};

  // universal for all appointment types
  if (!isNew && newValues.updatedAt) {
    variables.updatedAt = moment.utc(newValues.updatedAt).toISOString();
  }
  if (isNew || startHasChanged) {
    variables.start = moment(newValues.start).format(dateTimeLongFmt);
  }
  if (isNew || endHasChanged) {
    variables.end = getEndTime(newValues);
  }
  if (isNew || newValues.notes) {
    variables.notes = newValues.notes;
  }

  if (isNew || newValues.statusType) {
    variables.status = getAppointmentStatusEnums(newValues.statusType ?? 0);
  }

  const slotId = getSlotId(newValues);
  if (slotId !== oldValues?.slot?.id) {
    variables.slot = slotId;
  }

  if (isNew) {
    // store only needed on creation, does not change after
    variables.store = gql2Id(storeId);
  } else {
    // id for update
    variables.appointmentId = appointmentId;
  }

  if (isCustomerAppointment) {
    // unavailable blocks do not have these
    if (isNew) {
      variables.sendConfirmationEmail = sendConfirmationEmail;
    }
    if (isNew) {
      variables.customer = getCustomerId(newValues);
    }
    if (isNew || newValues.purchasableItem !== oldValues?.purchasableItem?.id) {
      variables.purchasableItem = getPiId(newValues);
    }

    const newIngredients = JSON.stringify(newValues.dripIngredients || []);
    const oldIngredients = oldValues?.dripIngredients || [];
    if (isNew || newIngredients !== oldIngredients) {
      variables.dripIngredients = getDripIng(newValues);
    }

    if (isNew || newValues.isFirstTime !== oldValues?.isFirstTime) {
      variables.isFirstTime = newValues.isFirstTime;
    }
  } else {
    variables.purchasableItem = NO_PRICE_ITEM_ID;
  }
  return variables;
};

export type SchedulableItems = {
  bundleCount: number | null;
  callback: string | null;
  description: string;
  duration: number;
  id: string;
  paddingBeforeVirgin: number;
};

export type PurchasableItemsPerSlot = {
  [key: number]: Record<number, boolean>;
};

export type SchedulableItemsInfo = {
  slotItems: PurchasableItemsPerSlot;
  loading: boolean;
};

const itemsPerSlot: PurchasableItemsPerSlot = {};

type TherapistHourPair = { start: string; end: string };

type AppointmentSlotApi = {
  idEncoded: string;
  shortDescription: string;
  sortOrder: number;
  therapistHours: Record<number, TherapistHourPair[]>;
};

export type CalendarResource = {
  id: string;
  title: string;
  sortOrder: number;
  extendedProps: { slotData: AppointmentSlotApi & { id: string } };
  businessHours: ResourceHours[];
};

const toBusinessHours = (
  therapistHours: Record<number, TherapistHourPair[]>
): ResourceHours[] => {
  const dayOfWeekIndicies = [0, 1, 2, 3, 4, 5, 6];
  const businessHours: ResourceHours[] = [];

  for (const day of dayOfWeekIndicies) {
    const hours = therapistHours?.[day];
    if (Array.isArray(hours)) {
      for (const pair of hours) {
        businessHours.push({
          daysOfWeek: [day],
          startTime: pair.start,
          endTime: pair.end
        });
      }
    } else {
      businessHours.push({
        daysOfWeek: [day],
        startTime: '00:00',
        endTime: '00:00'
      });
    }
  }
  return businessHours;
};

export const fetchAppointmentSlots = async (): Promise<CalendarResource[]> => {
  const response = await formDataInstance.get(
    `${CONFIG.BOOKING_API_URL}/staff/slots`
  );
  const data = get(response, 'data.data', []) as AppointmentSlotApi[];
  const resources: CalendarResource[] = data.map(aptSlot => ({
    id: aptSlot.idEncoded,
    title: aptSlot.shortDescription,
    sortOrder: aptSlot.sortOrder,
    extendedProps: { slotData: { ...aptSlot, id: aptSlot.idEncoded } },
    businessHours: toBusinessHours(aptSlot.therapistHours)
  }));
  if (CONFIG.APPT_TEST_HARNESS) {
    SetupTestHarness(
      TEST_HARNESS_MODULES.APPOINTMENTS,
      APPT_TEST_LABELS.slots,
      resources
    );
  }
  return resources;
};

const PurchasableItemSchema = z.object({
  id: z.number(),
  idEncoded: z.string(),
  items: z.array(z.any()),
  shortDescription: z.string(),
  sortOrder: z.number(),
  therapistHours: z.record(
    z.string(),
    z.array(z.object({ start: z.string(), end: z.string() }))
  )
});

type PurchasableItem = z.infer<typeof PurchasableItemSchema>;

const GetAppointmentSlotDataSchema = z.object({
  data: z.array(PurchasableItemSchema)
});

export const getAppointmentSlotItems =
  async (): Promise<PurchasableItemsPerSlot> => {
    let items: PurchasableItem[] = [];
    try {
      const res = await formDataInstance.get(
        CONFIG.BOOKING_API_URL + `/staff/appointment-slots/purchasable-items`
      );
      items = GetAppointmentSlotDataSchema.parse(res.data).data;
    } catch (error) {
      console.error(error);
      return itemsPerSlot;
    }

    const mapPurchasableItems = (
      items: PurchasableItem['items']
    ): Record<number, boolean> => {
      return items.reduce(
        (acc, item) => {
          acc[item.id] = true;
          return acc;
        },
        {} as Record<number, boolean>
      );
    };

    return items.reduce((acc, item) => {
      acc[item.id] = mapPurchasableItems(item.items);
      return acc;
    }, {} as PurchasableItemsPerSlot);
  };

export function getAppointmentStatusType(
  status: number
): AppointmentEventStatus | undefined {
  if (status & APPT_STATUS.CANCELLED) {
    return 'CANCELLED_OR_LATE';
  }
  if (status & APPT_STATUS.PURCHASED) {
    return 'PURCHASED';
  }
  if (status & APPT_STATUS.PENDING_TICKET) {
    return 'PENDING_TICKET';
  }
  if (status & APPT_STATUS.UNAVAILABLE) {
    return 'UNAVAILABLE';
  }
  if (status & APPT_STATUS.NO_SHOW) {
    return 'CANCELLED_OR_LATE';
  }
  if (status & APPT_STATUS.ARRIVED) {
    return 'ARRIVED';
  }
  if (status & APPT_STATUS.CONFIRMED) {
    return 'CONFIRMED';
  }
  if (status & APPT_STATUS.BOOKED) {
    return 'BOOKED';
  }

  return undefined;
}

export function applyStatusFlagsToMask(
  flags: StaffUpdateAppointmentRequestStatus,
  currentMask: number
): number {
  let mask = currentMask ?? 0;
  const setBit = (bit: number, value: boolean | undefined) => {
    if (value === undefined) {
      return;
    }
    mask = value ? mask | bit : mask & ~bit;
  };

  setBit(APPT_STATUS.BOOKED, flags.BOOKED);
  setBit(APPT_STATUS.ARRIVED, flags.ARRIVED);
  setBit(APPT_STATUS.CONFIRMED, flags.CONFIRMED);
  setBit(APPT_STATUS.PENDING_TICKET, flags.PENDING_TICKET);
  setBit(APPT_STATUS.PURCHASED, flags.PURCHASED);
  setBit(APPT_STATUS.CANCELLED, flags.CANCELLED);
  setBit(APPT_STATUS.NEW_CUSTOMER, flags.NEW_CUSTOMER);
  setBit(APPT_STATUS.UNAVAILABLE, flags.UNAVAILABLE);
  setBit(APPT_STATUS.NO_SHOW, flags.NO_SHOW);

  return mask;
}

export const getNutrientCreditCost = (
  nutrientData: NutrientInfo,
  id: number | string
): number => {
  const nutrientId = typeof id === 'number' ? id : gql2Id(id);
  return nutrientData.creditCostPerNutrient[nutrientId] ?? 0;
};
